import {
  useUncontrolledInput,
  useDebouncedValue,
  usePrevious,
  useDidMountValue
} from '@libs/ui/ds'
import { isString } from 'lodash-es'
import type { ChangeEvent } from 'react'
import { useEffect } from 'react'

type Params = {
  value?: string
  defaultValue?: string
  onChange?: (value: ChangeEvent) => void
  onDebouncedChange?: (str: string) => void
}

export const useDebouncedControlledState = <T extends Params>({
  value,
  defaultValue,
  onChange,
  debounceDelay,
  onDebouncedChange
}: T & { debounceDelay: number }) => {
  const [controlledValue, setControlledValue] = useUncontrolledInput({
    value,
    defaultValue,
    onChange
  })

  const isMounted = useDidMountValue()
  const [debouncedValue] = useDebouncedValue(controlledValue, debounceDelay)
  const previousDebouncedValue = usePrevious(debouncedValue)

  useEffect(() => {
    const differentValues = debouncedValue !== previousDebouncedValue
    const isDefined = isString(debouncedValue) && onDebouncedChange

    if (differentValues && isDefined && isMounted) {
      onDebouncedChange(debouncedValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, isMounted, previousDebouncedValue])

  return [controlledValue, setControlledValue] as const
}
